// hooks/useAudioPreloader.js
import { useEffect, useRef, useState } from "react";
import { Howl, Howler } from "howler";
import { useSelector } from "react-redux";
import { NARRATION_PATHS } from "../data/narrationPaths";

// Create a singleton cache for Howl instances
const howlCache = new Map();
let globalVolume = 1;

// Initialize Howl instance with proper configuration
const createHowl = (src, volume = globalVolume) => {
  return new Howl({
    src: [src],
    html5: false, // Use Web Audio API for better caching
    preload: true,
    volume: volume,
  });
};

// Get or create a Howl instance
export const getHowlInstance = (spriteKey, volume = globalVolume) => {
  const SPRITE_MAP = {
    // Onboarding
    code_generation: NARRATION_PATHS.onboarding.codeGeneration,
    after_code_generation: NARRATION_PATHS.onboarding.afterCodeGeneration,
    welcome_board: NARRATION_PATHS.onboarding.welcomeBoard,
    welcome_board_intro: NARRATION_PATHS.onboarding.welcomeBoard,
    level_rookie: NARRATION_PATHS.onboarding.levelRookie,
    level_intermediate: NARRATION_PATHS.onboarding.levelIntermediate,
    level_advanced: NARRATION_PATHS.onboarding.levelAdvanced,
    objective_explanation: NARRATION_PATHS.onboarding.objectiveExplanation,

    // Map
    map_intro: NARRATION_PATHS.system.mapIntro,
    hover_level: NARRATION_PATHS.system.mapHoverLevel,
    setting_intro: NARRATION_PATHS.system.mapSettingsIntro,
    clue_board: NARRATION_PATHS.system.mapClueBoard,
    sign_out: NARRATION_PATHS.system.mapSignOut,

    // Mission 1
    m1_intro: NARRATION_PATHS.mission1.intro,
    m1_level_selection: NARRATION_PATHS.mission1.levelSelection,
    selection_level: NARRATION_PATHS.mission1.levelSelection,
    m1_instructions_intro: NARRATION_PATHS.mission1.instructionsIntro,
    inM1_intro: NARRATION_PATHS.mission1.instructionsIntro,
    m1_intro_sec: NARRATION_PATHS.mission1.introSec,
    m1_control_hints: NARRATION_PATHS.mission1.controlHints,
    hint_icon: NARRATION_PATHS.mission1.controlHints,
    m1_control_save: NARRATION_PATHS.mission1.controlSave,
    save_icon: NARRATION_PATHS.mission1.controlSave,
    m1_control_stop: NARRATION_PATHS.mission1.controlStop,
    stop_icon: NARRATION_PATHS.mission1.controlStop,
    m1_control_back: NARRATION_PATHS.mission1.controlBack,
    back_to_map: NARRATION_PATHS.mission1.controlBack,
    m1_good_luck: NARRATION_PATHS.mission1.goodLuck,
    m1_rookie: NARRATION_PATHS.mission1.rookieInstructions,
    m1_intermediate: NARRATION_PATHS.mission1.intermediateInstructions,
    m1_advanced: NARRATION_PATHS.mission1.advancedInstructions,

    // Mission 2
    m2_intro: NARRATION_PATHS.mission2.intro,
    m2_rookie: NARRATION_PATHS.mission2.rookieInstructions,
    m2_intermediate: NARRATION_PATHS.mission2.intermediateInstructions,
    m2_advanced: NARRATION_PATHS.mission2.advancedInstructions,

    // Mission 3
    m3_intro: NARRATION_PATHS.mission3.intro,
    m3_rookie: NARRATION_PATHS.mission3.rookieInstructions,
    m3_intermediate: NARRATION_PATHS.mission3.intermediateInstructions,
    m3_advanced: NARRATION_PATHS.mission3.advancedInstructions,

    // Mission 4
    m4_intro: NARRATION_PATHS.mission4.intro,
    m4_rookie: NARRATION_PATHS.mission4.rookieInstructions,
    m4_intermediate: NARRATION_PATHS.mission4.intermediateInstructions,
    m4_advanced: NARRATION_PATHS.mission4.advancedInstructions,

    // Exit flow
    game_finish_p1: NARRATION_PATHS.exit.gameFinishP1,
    game_finish_p2: NARRATION_PATHS.exit.gameFinishP2,

    // Mission 1
    mission_1_rookieR: NARRATION_PATHS.clues.m1RookieReal,
    mission_1_rookieD: NARRATION_PATHS.clues.m1RookieDecoy,
    mission_1_intermediateR: NARRATION_PATHS.clues.m1IntermediateReal,
    mission_1_intermediateD: NARRATION_PATHS.clues.m1IntermediateDecoy,
    mission_1_advancedR: NARRATION_PATHS.clues.m1AdvancedReal,
    mission_1_advancedD: NARRATION_PATHS.clues.m1AdvancedDecoy,

    // Mission 2
    mission_2_rookieR: NARRATION_PATHS.clues.m2RookieReal,
    mission_2_rookieD: NARRATION_PATHS.clues.m2RookieDecoy,
    mission_2_intermediateR: NARRATION_PATHS.clues.m2IntermediateReal,
    mission_2_intermediateD: NARRATION_PATHS.clues.m2IntermediateDecoy,
    mission_2_advancedR: NARRATION_PATHS.clues.m2AdvancedReal,
    mission_2_advancedD: NARRATION_PATHS.clues.m2AdvancedDecoy,

    // Mission 3
    mission_3_rookieR: NARRATION_PATHS.clues.m3RookieReal,
    mission_3_rookieD: NARRATION_PATHS.clues.m3RookieDecoy,
    mission_3_intermediateR: NARRATION_PATHS.clues.m3IntermediateReal,
    mission_3_intermediateD: NARRATION_PATHS.clues.m3IntermediateDecoy,
    mission_3_advancedR: NARRATION_PATHS.clues.m3AdvancedReal,
    mission_3_advancedD: NARRATION_PATHS.clues.m3AdvancedDecoy,

    // Mission 4
    mission_4_rookieR: NARRATION_PATHS.clues.m4RookieReal,
    mission_4_rookieD: NARRATION_PATHS.clues.m4RookieDecoy,
    mission_4_intermediateR: NARRATION_PATHS.clues.m4IntermediateReal,
    mission_4_intermediateD: NARRATION_PATHS.clues.m4IntermediateDecoy,
    mission_4_advancedR: NARRATION_PATHS.clues.m4AdvancedReal,
    mission_4_advancedD: NARRATION_PATHS.clues.m4AdvancedDecoy,
  };

  const src = SPRITE_MAP[spriteKey];
  if (!src) {
    console.warn(`Unknown sprite key: ${spriteKey}`);
    return null;
  }

  // Return cached instance if exists
  if (howlCache.has(spriteKey)) {
    const howl = howlCache.get(spriteKey);
    howl.volume(volume); // Update volume
    return howl;
  }

  // Create and cache new instance
  const howl = createHowl(src, volume);
  howlCache.set(spriteKey, howl);
  return howl;
};

// Update global volume for all instances
export const updateGlobalVolume = (volume) => {
  globalVolume = volume;
  Howler.volume(volume);
  howlCache.forEach((howl) => howl.volume(volume));
};

// Cleanup all instances (call on app unmount)
export const cleanupAllHowls = () => {
  howlCache.forEach((howl) => {
    try {
      howl.unload();
    } catch (e) {
      console.warn("Error unloading howl:", e);
    }
  });
  howlCache.clear();
};

/**
 * Preload specific audio files
 * Returns loading state and progress
 */
export const useAudioPreloader = (spriteKeys = []) => {
  const [loading, setLoading] = useState(true);
  const [progress, setProgress] = useState(0);
  const [errors, setErrors] = useState([]);
  const { masterVolume } = useSelector((s) => s.settings);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const volume = (Number(masterVolume) || 0) / 100;
    updateGlobalVolume(volume);
  }, [masterVolume]);

  useEffect(() => {
    if (spriteKeys.length === 0) {
      setLoading(false);
      setProgress(100);
      return;
    }

    let loadedCount = 0;
    const totalFiles = spriteKeys.length;
    const newErrors = [];

    const updateProgress = () => {
      if (!mountedRef.current) return;
      const pct = Math.round((loadedCount / totalFiles) * 100);
      setProgress(pct);
      if (pct === 100) {
        setLoading(false);
      }
    };

    spriteKeys.forEach((key) => {
      const howl = getHowlInstance(key, (Number(masterVolume) || 0) / 100);

      if (!howl) {
        loadedCount++;
        newErrors.push({ key, error: "Invalid sprite key" });
        updateProgress();
        return;
      }

      // Check if already loaded
      if (howl.state() === "loaded") {
        loadedCount++;
        updateProgress();
        return;
      }

      // Listen for load completion
      const onLoad = () => {
        loadedCount++;
        updateProgress();
        howl.off("load", onLoad);
        howl.off("loaderror", onError);
      };

      const onError = (id, err) => {
        loadedCount++;
        newErrors.push({ key, error: err });
        updateProgress();
        howl.off("load", onLoad);
        howl.off("loaderror", onError);
      };

      howl.once("load", onLoad);
      howl.once("loaderror", onError);
    });

    setErrors(newErrors);

    return () => {
      // Cleanup handled globally
    };
  }, [spriteKeys, masterVolume]);

  return { loading, progress, errors };
};
